import {FC} from 'react'

import {Dialog, DialogContent} from '@components/ui/dialog'
import {Sheet, SheetContent} from '@components/ui/sheet'
import {Opportunity} from '@features/insights/types'
import {useMediaQuery} from '@hooks/use-media-query'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@components/ui/button'
import {ChevronLeftIcon, ChevronRightIcon} from '@components/ui/icon'
import {ReviewFeedbackImages} from './review-feedback-images'
import {ModalFooter, ModalHeader} from '@components/ui/modal-atoms'
import {ReviewFeedbackDefinition} from './review-feedback-definition'

interface ReviewFeedbackModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onNext: () => void
    onPrev: () => void
    isPrevDisabled: boolean
    isNextDisabled: boolean
    opportunity?: Opportunity
}

export const ReviewFeedbackModal: FC<ReviewFeedbackModalProps> = ({
    open,
    onOpenChange,
    opportunity,
    onNext,
    onPrev,
    isPrevDisabled,
    isNextDisabled
}) => {
    const isLarge = useMediaQuery({breakpoint: 'md'})

    const content = opportunity && (
        <ModalContent
            opportunity={opportunity}
            onClose={() => onOpenChange(false)}
            onNext={onNext}
            onPrev={onPrev}
            isPrevDisabled={isPrevDisabled}
            isNextDisabled={isNextDisabled}
            isMobile={!isLarge}
        />
    )

    if (isLarge) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent showCloseButton={false} className="h-[calc(100dvh-(--spacing(12)))] w-[1230px]">
                    {content}
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent showCloseButton={false} className="h-[calc(100%-(--spacing(5)))]">
                {content}
            </SheetContent>
        </Sheet>
    )
}

interface ModalContentProps {
    onClose: () => void
    onNext: () => void
    onPrev: () => void
    isPrevDisabled: boolean
    isNextDisabled: boolean
    opportunity: Opportunity
    isMobile: boolean
}

export const ModalContent: FC<ModalContentProps> = ({
    onClose,
    opportunity,
    onNext,
    onPrev,
    isPrevDisabled,
    isNextDisabled,
    isMobile
}) => {
    const {t} = useTranslation()

    return (
        <div className="grid grid-rows-[auto_minmax(0,1fr)_auto] h-full">
            <ModalHeader className="px-6 py-4 text-lg font-medium">
                {t(`insights:issues:reason_title:${opportunity.keyName}`)}
            </ModalHeader>

            <div className="flex flex-col overflow-auto md:grid md:grid-cols-[2fr_1fr] md:grid-rows-[minmax(0,1fr)] h-full md:overflow-hidden">
                <ReviewFeedbackImages
                    images={opportunity.images.slice(0, 6)}
                    opportunityKeyName={opportunity.keyName}
                    isMobile={isMobile}
                />

                <ReviewFeedbackDefinition definitionKey={opportunity.keyName} isMobile={isMobile} />
            </div>

            <ModalFooter>
                <Button variant="tertiary" onClick={onClose}>
                    {t('commons:close')}
                </Button>
                <div className="flex items-center gap-2.5">
                    <Button shape="square" variant="primary" onClick={onPrev} disabled={isPrevDisabled}>
                        <ChevronLeftIcon />
                    </Button>

                    <Button shape="square" variant="primary" onClick={onNext} disabled={isNextDisabled}>
                        <ChevronRightIcon />
                    </Button>
                </div>
            </ModalFooter>
        </div>
    )
}
